import { createFileRoute } from '@tanstack/react-router';
import { Group, Loader, Stack, Text, rem } from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { IconAlertTriangle, IconClock, IconLogin, IconLogout, IconRefresh } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { AttendanceCalendar } from '@/features/attendance/AttendanceCalendar';
import { useMyEmployeeId } from '@/features/workforce/useMyEmployee';
import { fetchCurrentStatus, fetchAttendanceRange, parseBeIso } from '@/features/attendance/api';
import { useAttendanceQueue } from '@/features/attendance/useAttendanceQueue';
import { AttendanceQueuePanel } from '@/features/attendance/AttendanceQueuePanel';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { DesktopAttendance } from '@/features/attendance/desktop/DesktopAttendance';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { MobileSegmented } from '@/shared/mobile/MobileSegmented';
import { mw, mwSectionLabel, MW_FONT_NUM } from '@/shared/mobile/tokens';
import { useMinuteBeat } from '@/shared/lib/battitoMinuto';

export const Route = createFileRoute('/_app/attendance')({ component: AttendanceRoute });

/** Selettore: Presenze desktop ridisegnata vs pagina mobile/tab esistente. */
function AttendanceRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <AttendancePage /> : <DesktopAttendance />;
}

const CARD = { border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface } as const;

function AttendancePage() {
  const [tab, setTab] = useState<'today' | 'calendar'>('today');
  return (
    <Stack gap={16} px={20} pt={16}>
      <MobileHeader title="Presenze" />
      <MobileSegmented<'today' | 'calendar'>
        value={tab}
        onChange={setTab}
        options={[{ value: 'today', label: 'Oggi' }, { value: 'calendar', label: 'Calendario' }]}
      />
      {tab === 'today' ? <TodayPanel /> : <AttendanceCalendar />}
    </Stack>
  );
}

function formatHm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function TodayPanel() {
  const qc = useQueryClient();
  const myEmployeeId = useMyEmployeeId();
  /*
   * ⚠️ Qui c'era un `setInterval` da **trenta** secondi, e non era «mezza precisione»: un
   * intervallo fisso non allineato lascia l'orologio indietro fino a mezzo minuto, sempre, con il
   * doppio dei ridisegni di quelli che servono. I valori qui sotto sono minuti
   * (`diff(startedAt, 'minute')`), quindi la granularita' giusta e' il minuto — allo scoccare.
   */
  const now = dayjs(useMinuteBeat());

  const statusQ = useQuery({
    queryKey: ['attendance-status'],
    queryFn: () => fetchCurrentStatus(),
    refetchInterval: 60_000,
  });

  const todayIso = dayjs().format('YYYY-MM-DD');
  const todayEventsQ = useQuery({
    queryKey: ['attendance-today', myEmployeeId, todayIso],
    enabled: Boolean(myEmployeeId),
    queryFn: () => fetchAttendanceRange(
      myEmployeeId!,
      dayjs().startOf('day').toISOString(),
      dayjs().endOf('day').toISOString(),
    ),
    refetchInterval: 60_000,
  });

  const isIn = statusQ.data?.status === 'in';
  const startedAt = statusQ.data?.lastTimestamp && isIn ? dayjs(parseBeIso(statusQ.data.lastTimestamp)) : null;
  const sessionMinutes = startedAt ? now.diff(startedAt, 'minute') : 0;

  // Totale minuti lavorati oggi (somma di tutti i pair in/out chiusi + sessione corrente se aperta)
  const todayTotalMinutes = useMemo(() => {
    const events = (todayEventsQ.data ?? []).slice().sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    let total = 0;
    let lastIn: string | null = null;
    for (const ev of events) {
      if (ev.status === 1) lastIn = ev.timestamp;
      else if (ev.status === 0 && lastIn) {
        total += Math.max(0, (parseBeIso(ev.timestamp).getTime() - parseBeIso(lastIn).getTime()) / 60000);
        lastIn = null;
      }
    }
    if (lastIn) {
      total += Math.max(0, now.toDate().getTime() - parseBeIso(lastIn).getTime()) / 60000;
    }
    return Math.round(total);
  }, [todayEventsQ.data, now]);

  /* La rete di sicurezza: se il `POST` fallisce perché la copertura non c'è, il gesto viene
     salvato sul dispositivo con l'ora in cui è avvenuto e riparte alla riconnessione. Un
     rifiuto del server (duplicato, sessione scaduta) risale invece come rifiuto: non si
     risolve aspettando. */
  const queue = useAttendanceQueue(myEmployeeId);


  // Filtra al giorno LOCALE corrente (il fetch range usa start/endOfDay locale + toISOString che e' UTC,
  // quindi puo' restituire timbrature di ieri o domani in altri TZ; rifiltriamo qui per sicurezza).
  const todayLocal = dayjs().format('YYYY-MM-DD');
  const eventsToday = (todayEventsQ.data ?? [])
    .filter((e) => dayjs(parseBeIso(e.timestamp)).format('YYYY-MM-DD') === todayLocal)
    .slice()
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const lastEvent = eventsToday[eventsToday.length - 1];
  const hasOpenSession = lastEvent?.status === 1;


  return (
    <Stack gap={16}>
      <AttendanceQueuePanel queue={queue} />

      {/* Lo stato corrente, in sola lettura: in servizio o no, da quanto, da che ora. Il pulsante
          per timbrare non c'e piu (vedi la nota qui sotto). Finche lo stato non e arrivato gira la
          rotella: prima si leggeva «FUORI SERVIZIO» anche a chi era dentro, e chi ci credeva
          timbrava un'entrata doppia. */}
      <div style={{ ...CARD, padding: 22, textAlign: 'center', background: isIn ? '#1B1B22' : mw.surface, border: isIn ? 0 : `1px solid ${mw.line}` }}>
        {statusQ.isLoading ? <InlineLoader label="Verifica dello stato di timbratura…" minHeight={168} /> : (
        <>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 'var(--fs-compact)', fontWeight: 700, letterSpacing: 0.4, padding: '5px 14px', borderRadius: 999, background: isIn ? 'rgba(30,139,87,0.22)' : mw.surface2, color: isIn ? '#7DE0AC' : mw.ink500 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: isIn ? mw.success : mw.ink300 }} />
          {isIn ? 'IN SERVIZIO' : 'FUORI SERVIZIO'}
        </span>
        <div style={{ marginTop: 14, fontFamily: MW_FONT_NUM, fontSize: 'var(--fs-hero)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, color: isIn ? '#fff' : mw.ink900 }}>
          {isIn ? formatHm(sessionMinutes) : '—'}
        </div>
        <Text style={{ fontSize: 'var(--fs-compact)', color: isIn ? 'rgba(255,255,255,0.6)' : mw.ink500, marginTop: 6 }}>
          {startedAt
            ? `Entrato alle ${startedAt.format('HH:mm')}`
            /* Non piu «Timbra la tua entrata per iniziare»: da qui non si timbra, e un invito a
               fare una cosa che la schermata non offre e' peggio di nessun invito. */
            : 'Nessuna entrata registrata oggi — si timbra dalla Home'}
        </Text>
        </>
        )}
      </div>

      {/*
        QUI NON SI TIMBRA.

        C'erano il pulsante grande e i tre modi (tag NFC, QR, perimetro GPS). Erano un **duplicato**:
        la Home monta `ScelteTimbratura`, che copre gia l'entrata nei tre modi (sede col tag, smart
        working, trasferta) e la chiusura del turno, con lo stesso cooldown anti-duplicato. Due
        posti da cui timbrare la stessa giornata non sono una comodita: sono due stati da tenere
        d'accordo, e la domanda «da quale dei due ho timbrato?» quando qualcosa non torna.

        Questa schermata resta il **registro**: stato corrente, ore di oggi, le timbrature della
        giornata e il calendario del mese. Cioe si legge, non si scrive.

        Resta anche il pannello della coda offline, qui sopra: non e un modo per timbrare, e la
        sorte di timbrature **gia** fatte che aspettano la rete — e vive solo su questa schermata,
        quindi togliendolo le timbrature in attesa diventerebbero invisibili.
      */}

      {/* Stat giornata */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        <div style={{ ...CARD, borderRadius: 14, padding: '12px 14px' }}>
          <Text component="div" style={{ ...mwSectionLabel, fontSize: 'var(--fs-compact)' }}>Sessione corrente</Text>
          {/* Un totale a zero mentre la lettura è in corso è un numero falso, non un numero
              basso: finché non arriva, gira la rotella. */}
          {statusQ.isLoading ? <Loader size="xs" color={mw.brand} mt={6} /> : (
            <Text style={{ fontSize: 'var(--fs-h3)', fontWeight: 700, color: isIn ? mw.success : mw.ink500, marginTop: 3, fontFamily: MW_FONT_NUM }}>{isIn ? formatHm(sessionMinutes) : '—'}</Text>
          )}
        </div>
        <div style={{ ...CARD, borderRadius: 14, padding: '12px 14px' }}>
          <Text component="div" style={{ ...mwSectionLabel, fontSize: 'var(--fs-compact)' }}>Totale oggi</Text>
          {todayEventsQ.isLoading ? <Loader size="xs" color={mw.brand} mt={6} /> : (
            <Text style={{ fontSize: 'var(--fs-h3)', fontWeight: 700, color: mw.ink900, marginTop: 3, fontFamily: MW_FONT_NUM }}>{formatHm(todayTotalMinutes)}</Text>
          )}
        </div>
      </div>

      {/* Timbrature di oggi */}
      <div>
        <Group justify="space-between" mb={8}>
          <Text component="div" style={{ ...mwSectionLabel, paddingLeft: 2 }}>Timbrature di oggi</Text>
          <button className="mw-pressable" onClick={() => { qc.invalidateQueries({ queryKey: ['attendance-status'] }); qc.invalidateQueries({ queryKey: ['attendance-today'] }); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: 0, background: 'transparent', cursor: 'pointer', color: mw.accentText, fontSize: 'var(--fs-compact)', fontWeight: 600, fontFamily: 'inherit' }}>
            <IconRefresh size={14} /> Aggiorna
          </button>
        </Group>
        {todayEventsQ.isError ? <QueryErrorAlert error={todayEventsQ.error} onRetry={() => { void todayEventsQ.refetch(); }} /> : todayEventsQ.isLoading ? (
          <div style={{ ...CARD, padding: 16 }}><InlineLoader label="Caricamento timbrature di oggi…" minHeight={70} /></div>
        ) : eventsToday.length === 0 ? (
          <div style={{ ...CARD, padding: '26px 16px', textAlign: 'center' }}>
            <span style={{ display: 'inline-flex', width: 48, height: 48, borderRadius: 13, background: mw.surface2, color: mw.ink300, alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}><IconClock size={24} /></span>
            <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>Nessuna timbratura oggi</Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 2 }}>Entrate e uscite compariranno qui.</Text>
          </div>
        ) : (
          <div style={{ ...CARD, padding: '4px 14px' }}>
            {eventsToday.map((ev, i) => {
              const inn = ev.status === 1;
              return (
                <div key={`${ev.timestamp}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 2px', borderTop: i > 0 ? `1px solid ${mw.line}` : undefined }}>
                  <span style={{ width: 34, height: rem(34), borderRadius: 10, flexShrink: 0, background: inn ? mw.successBg : mw.dangerBg, color: inn ? mw.success : mw.danger, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {inn ? <IconLogin size={17} /> : <IconLogout size={17} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>{inn ? 'Entrata' : 'Uscita'}</Text>
                    {ev.locationType != null && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{ev.locationType === 0 ? 'In sede' : ev.locationType === 1 ? 'Da remoto' : `Loc ${ev.locationType}`}</Text>}
                  </div>
                  <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900, fontFamily: MW_FONT_NUM }}>{dayjs(parseBeIso(ev.timestamp)).format('HH:mm')}</Text>
                </div>
              );
            })}
          </div>
        )}
        {hasOpenSession && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: mw.accentTint, borderRadius: 14, padding: '12px 16px', marginTop: 10 }}>
            <IconAlertTriangle size={18} style={{ color: mw.accentText, flexShrink: 0, marginTop: 1 }} />
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
              Sessione aperta dalle <strong>{dayjs(parseBeIso(lastEvent!.timestamp)).format('HH:mm')}</strong> — ricorda di timbrare l&apos;uscita a fine giornata.
            </Text>
          </div>
        )}
      </div>
    </Stack>
  );
}
